import React, { ReactNode, useState } from "react";
import Text from "./Text";
import { ShimmerLoader } from "./ShimmerLoader";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import Icon from "./Icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const Charts = ["line", "area", "bar", "pie"] as const;

type ChartType = (typeof Charts)[number];

type DropDownFilterProps = {
  dropDownFilter: true;
  groupOn: string;
  setGroupOn: (value: string) => void;
  GroupByFilters: { value: string; label: string }[];
};

type NoDropDownFilterProps = {
  dropDownFilter?: false;
  groupOn?: never;
  setGroupOn?: never;
  GroupByFilters?: never;
};

type GeneralProps = {
  title: string;
  children: React.ReactNode;
  isLoading?: boolean;
  hasInfoIcon?: boolean;
  infoText?: string;
  className?: string;
  legendComponent?: () => ReactNode;
  onInfoIconClick?: () => void;
  chartType?: ChartType;
  redacted?: boolean;
};
type ChartWrapperProps = GeneralProps &
  (DropDownFilterProps | NoDropDownFilterProps);

export default function ChartWrapper({
  title,
  children,
  hasInfoIcon,
  infoText,
  isLoading,
  className,
  onInfoIconClick,
  dropDownFilter,
  groupOn,
  legendComponent,
  setGroupOn,
  GroupByFilters,
  chartType,
  redacted = false,
}: ChartWrapperProps) {
  const [showPopover, setShowPopover] = useState(false);

  const LegendComponent = legendComponent;

  return (
    <div className="flex w-full flex-col gap-2">
      <Popover open={showPopover}>
        <PopoverTrigger asChild>
          <Card className={cn("min-h-[139px] min-w-[180px] px-4", className)}>
            {redacted && chartType && (
              <>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-start  text-base font-semibold  leading-tight text-text-primary">
                        {title}
                      </CardTitle>
                      {hasInfoIcon && (
                        <motion.button
                          className="stroke-text-secondary"
                          onHoverStart={() => {
                            setShowPopover(true);
                          }}
                          onHoverEnd={() => {
                            setShowPopover(false);
                          }}
                          onClick={() => {
                            setShowPopover(true);
                            onInfoIconClick?.();
                          }}
                        >
                          <Icon name="info" size={14} />
                        </motion.button>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </>
            )}
            {!redacted && (
              <>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-start  text-base font-semibold  leading-tight text-text-primary">
                        {title}
                      </CardTitle>
                      {hasInfoIcon && (
                        <motion.button
                          className="stroke-text-secondary"
                          onHoverStart={() => {
                            setShowPopover(true);
                          }}
                          onHoverEnd={() => {
                            setShowPopover(false);
                          }}
                          onClick={() => {
                            setShowPopover(true);
                            onInfoIconClick?.();
                          }}
                        >
                          <Icon name="info" size={14} />
                        </motion.button>
                      )}
                    </div>
                    {!isLoading && (
                      <div className="flex items-center gap-2 ">
                        {LegendComponent && <LegendComponent />}
                        {dropDownFilter && (
                          <Select
                            value={groupOn}
                            onValueChange={(value) => setGroupOn(value)}
                          >
                            <SelectTrigger
                              className=" h-7 max-w-fit rounded-none border-none bg-navigation-navItemBackSelected text-brand-accent focus:ring-transparent [&>span]:mr-1 [&>span]:text-left [&>span]:text-sm [&>span]:leading-none [&>svg]:stroke-brand-accent"
                              aria-label="Select a value"
                            >
                              <SelectValue placeholder="Select group" />
                            </SelectTrigger>
                            <SelectContent>
                              {GroupByFilters.map((filter) => (
                                <SelectItem
                                  key={filter.value}
                                  value={filter.value}
                                  className="pl-3 text-left text-brand-accent focus:text-brand-accent data-[state=checked]:font-semibold"
                                >
                                  {filter.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="text-left ">
                  {isLoading ? (
                    <ShimmerLoader height={250} width={250} />
                  ) : (
                    children
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" sideOffset={6}>
          <Text variant="normalText" className="text-text-secondary">
            {infoText}
          </Text>
        </PopoverContent>
      </Popover>
    </div>
  );
}
